import { useCallback, useEffect, useRef, useState } from "react";
// Кандидаты MIME для MediaRecorder по приоритету. Suno/бэк принимает multipart
// и конвертирует к нужному формату — на фронте берём первый поддерживаемый.
const MIME_CANDIDATES = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
    "audio/ogg",
];
const pickMimeType = () => {
    if (typeof MediaRecorder === "undefined" ||
        typeof MediaRecorder.isTypeSupported !== "function") {
        return undefined;
    }
    return MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m));
};
const extForMime = (mime) => {
    if (!mime)
        return "webm";
    if (mime.includes("mp4"))
        return "mp4";
    if (mime.includes("ogg"))
        return "ogg";
    return "webm";
};
const detectSupported = () => typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    typeof MediaRecorder !== "undefined";
export function useAudioRecorder(options) {
    const maxDurationMs = options?.maxDurationMs;
    const isSupported = detectSupported();
    const [state, setState] = useState(isSupported ? "idle" : "unsupported");
    const [stream, setStream] = useState(null);
    const [durationMs, setDurationMs] = useState(0);
    const [blob, setBlob] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [mimeType, setMimeType] = useState(null);
    const [error, setError] = useState(null);
    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const startedAtRef = useRef(0);
    const previewUrlRef = useRef(null);
    const clearTimer = () => {
        if (timerRef.current !== null) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };
    const stopStream = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setStream(null);
    };
    const revokePreview = () => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
    };
    // Полная очистка на размонтировании: микрофон, таймер, blob-URL.
    useEffect(() => {
        return () => {
            clearTimer();
            stopStream();
            revokePreview();
            try {
                recorderRef.current?.stop();
            }
            catch {
                /* уже остановлен */
            }
        };
    }, []);
    const start = useCallback(async () => {
        if (!isSupported) {
            setState("unsupported");
            return;
        }
        setError(null);
        // Сброс предыдущей записи перед новой.
        revokePreview();
        setPreviewUrl(null);
        setBlob(null);
        setDurationMs(0);
        chunksRef.current = [];
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        }
        catch (e) {
            const name = e?.name;
            if (name === "NotAllowedError" || name === "SecurityError") {
                setState("denied");
                setError("Доступ к микрофону отклонён");
            }
            else {
                setState("error");
                setError("Не удалось получить доступ к микрофону");
            }
            return;
        }
        streamRef.current = stream;
        setStream(stream);
        const mime = pickMimeType();
        let recorder;
        try {
            recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
        }
        catch {
            // Некоторые браузеры не принимают опции — пробуем без них.
            try {
                recorder = new MediaRecorder(stream);
            }
            catch {
                stopStream();
                setState("unsupported");
                setError("Запись не поддерживается в этом браузере");
                return;
            }
        }
        recorderRef.current = recorder;
        setMimeType(recorder.mimeType || mime || null);
        recorder.ondataavailable = (ev) => {
            if (ev.data && ev.data.size > 0)
                chunksRef.current.push(ev.data);
        };
        recorder.onstop = () => {
            clearTimer();
            stopStream();
            const type = recorder.mimeType || mime || "audio/webm";
            const finalBlob = new Blob(chunksRef.current, { type });
            setBlob(finalBlob);
            const url = URL.createObjectURL(finalBlob);
            previewUrlRef.current = url;
            setPreviewUrl(url);
            setDurationMs(Date.now() - startedAtRef.current);
            setState("recorded");
        };
        startedAtRef.current = Date.now();
        recorder.start();
        setState("recording");
        clearTimer();
        timerRef.current = setInterval(() => {
            const elapsed = Date.now() - startedAtRef.current;
            setDurationMs(elapsed);
            // Достигнут максимум длительности — мягкий авто-стоп (onstop дочистит поток
            // и зафиксирует финальную длительность).
            if (maxDurationMs != null && elapsed >= maxDurationMs) {
                const rec = recorderRef.current;
                if (rec && rec.state !== "inactive")
                    rec.stop();
            }
        }, 200);
    }, [isSupported, maxDurationMs]);
    const stop = useCallback(() => {
        const recorder = recorderRef.current;
        if (recorder && recorder.state !== "inactive") {
            recorder.stop();
        }
    }, []);
    const reset = useCallback(() => {
        clearTimer();
        stopStream();
        revokePreview();
        try {
            if (recorderRef.current && recorderRef.current.state !== "inactive") {
                recorderRef.current.stop();
            }
        }
        catch {
            /* noop */
        }
        recorderRef.current = null;
        chunksRef.current = [];
        setBlob(null);
        setPreviewUrl(null);
        setDurationMs(0);
        setError(null);
        setState(isSupported ? "idle" : "unsupported");
    }, [isSupported]);
    const toFile = useCallback((baseName) => {
        if (!blob)
            return null;
        const ext = extForMime(mimeType);
        return new File([blob], `${baseName}.${ext}`, {
            type: blob.type || mimeType || "audio/webm",
        });
    }, [blob, mimeType]);
    return {
        state,
        stream,
        durationMs,
        blob,
        previewUrl,
        mimeType,
        error,
        isSupported,
        start,
        stop,
        reset,
        toFile,
    };
}
